// Copyright (c) 2026 Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import {
  BffInboundMessage,
  RefereeAnswerDeltaEvent,
  RefereeAnswerDoneEvent,
  RefereeConversation,
  RefereeErrorEvent,
  RefereeHistoryEvent,
  getSharedBffWebSocket,
} from '@/services/bffWebSocket';

export type RefereeMessage = {
  id: string;
  role: 'user' | 'referee';
  content: string;
  /** True while deltas are still arriving for this answer. */
  streaming?: boolean;
};

/**
 * Generates a conversation id. The client owns it, per the requestId convention: the id travels
 * with the first question and the service creates the conversation under it.
 */
const newId = (): string => {
  const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof cryptoRef?.randomUUID === 'function') {
    return cryptoRef.randomUUID();
  }
  // RN's Hermes has no randomUUID on older runtimes; the id only has to be unique per user.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

type RefereeState = {
  messages: RefereeMessage[];
  conversations: RefereeConversation[];
  asking: boolean;
  errorCode: string | null;
  ask: (question: string) => void;
  startNewConversation: () => void;
  openConversation: (conversationId: string) => void;
  conversationId: string;
};

/**
 * Owns one referee conversation over the shared socket: sends `referee.ask`, accumulates
 * `referee.answer.delta` frames into the trailing message, and loads saved history on mount.
 */
export function useReferee(): RefereeState {
  const { locale } = useI18n();
  const socket = useMemo(() => getSharedBffWebSocket(getApiBaseUrl()), []);

  const [conversationId, setConversationId] = useState<string>(() => newId());
  const [messages, setMessages] = useState<RefereeMessage[]>([]);
  const [conversations, setConversations] = useState<RefereeConversation[]>([]);
  const [asking, setAsking] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // The in-flight request id, so a late frame from an abandoned question is ignored.
  const pendingRequestId = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = socket.onMessage((message: BffInboundMessage) => {
      const type = (message as { type?: string }).type;

      if (type === 'referee.answer.delta') {
        const event = message as RefereeAnswerDeltaEvent;
        if (event.requestId !== pendingRequestId.current) return;
        const text = event.text ?? '';
        if (!text) return;
        setMessages((current) => appendDelta(current, text));
        return;
      }

      if (type === 'referee.answer.done') {
        const event = message as RefereeAnswerDoneEvent;
        if (event.requestId !== pendingRequestId.current) return;
        pendingRequestId.current = null;
        setAsking(false);
        setMessages((current) =>
          current.map((entry) => (entry.streaming ? { ...entry, streaming: false } : entry))
        );
        socket.sendRefereeHistoryRequest({});
        return;
      }

      if (type === 'referee.error') {
        const event = message as RefereeErrorEvent;
        // History errors carry no requestId of ours; still worth surfacing.
        if (event.requestId && event.requestId !== pendingRequestId.current) return;
        pendingRequestId.current = null;
        setAsking(false);
        setErrorCode(event.code ?? 'referee.unavailable');
        // Drop the empty placeholder so the screen does not show a blank referee bubble.
        setMessages((current) => current.filter((entry) => !(entry.streaming && !entry.content)));
        return;
      }

      if (type === 'referee.history') {
        const event = message as RefereeHistoryEvent;
        setConversations(event.conversations ?? []);
      }
    });

    socket.sendRefereeHistoryRequest({});
    // Wrapped: onMessage's unsubscribe returns a boolean, which is not a valid effect cleanup.
    return () => {
      unsubscribe();
    };
  }, [socket]);

  const ask = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || pendingRequestId.current) return;

      const requestId = newId();
      pendingRequestId.current = requestId;
      setErrorCode(null);
      setAsking(true);
      setMessages((current) => [
        ...current,
        { id: `${requestId}-q`, role: 'user', content: trimmed },
        { id: `${requestId}-a`, role: 'referee', content: '', streaming: true },
      ]);

      const outcome = socket.sendRefereeAsk({
        requestId,
        conversationId,
        question: trimmed,
        locale,
      });

      // 'queued' is not a failure — the frame flushes on reopen. Only 'dropped' is terminal.
      if (outcome === 'dropped') {
        pendingRequestId.current = null;
        setAsking(false);
        setErrorCode('referee.unavailable');
        setMessages((current) => current.filter((entry) => !(entry.streaming && !entry.content)));
      }
    },
    [conversationId, locale, socket]
  );

  const startNewConversation = useCallback(() => {
    pendingRequestId.current = null;
    setConversationId(newId());
    setMessages([]);
    setAsking(false);
    setErrorCode(null);
  }, []);

  const openConversation = useCallback(
    (id: string) => {
      const conversation = conversations.find((entry) => entry.conversationId === id);
      if (!conversation) return;
      pendingRequestId.current = null;
      setConversationId(id);
      setAsking(false);
      setErrorCode(null);
      setMessages(
        (conversation.turns ?? []).map((turn) => ({
          id: turn.id,
          role: turn.role === 'referee' ? 'referee' : 'user',
          content: turn.content,
        }))
      );
    },
    [conversations]
  );

  return {
    messages,
    conversations,
    asking,
    errorCode,
    ask,
    startNewConversation,
    openConversation,
    conversationId,
  };
}

/** Appends a chunk to the trailing streaming message. Exported for the hook's tests. */
export function appendDelta(messages: RefereeMessage[], text: string): RefereeMessage[] {
  const lastIndex = messages.length - 1;
  if (lastIndex < 0 || !messages[lastIndex].streaming) return messages;
  const next = messages.slice();
  next[lastIndex] = { ...next[lastIndex], content: next[lastIndex].content + text };
  return next;
}
